import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useWindowSize } from '@uidotdev/usehooks';
import * as d from 'date-fns';

import { reportBudget } from 'loot-core/client/queries';
import * as monthUtils from 'loot-core/shared/months';
import { monthsOfFiscalYear } from 'loot-core/shared/months';
import {
  addNotification,
  applyBudgetAction,
  createCategory,
  createGroup,
  deleteCategory,
  deleteGroup,
  getCategories,
  moveCategory,
  moveCategoryGroup,
  pushModal,
  savePrefs,
  updateCategory,
  updateGroup,
} from 'loot-core/src/client/actions';
import { liveQuery } from 'loot-core/src/client/query-helpers';
import { useSpreadsheet } from 'loot-core/src/client/SpreadsheetProvider';
import { send } from 'loot-core/src/platform/client/fetch';
import { ApplyBudgetTemplateMode } from 'loot-core/src/server/budget-templates/types/template-apply-mode';
import { type BudgetTemplateLine } from 'loot-core/src/shared/budget-template';
import { q } from 'loot-core/src/shared/query';

import { useCategories } from '../../hooks/useCategories';
import { useGlobalPref } from '../../hooks/useGlobalPref';
import { useIsTablet } from '../../hooks/useIsTablet';
import { useLocalPref } from '../../hooks/useLocalPref';
import { SvgCog } from '../../icons/v1';
import { SvgArrowBackUp } from '../../icons/v2/ArrowBackUp';
import { SvgBarChart } from '../../icons/v2/BarChart';
import { SvgBudgetTemplateApply } from '../../icons/v2/BudgetTemplateApply';
import { SvgBudgetTemplateOverwrite } from '../../icons/v2/BudgetTemplateOverwrite';
import { SvgBudgetTemplatePopulateUsing } from '../../icons/v2/BudgetTemplatePopulateUsing';
import { SvgBudgetTemplateReset } from '../../icons/v2/BudgetTemplateReset';
import { SvgViewHide } from '../../icons/v2/ViewHide';
import { styles, theme } from '../../style';
import { prewarmAllMonths } from '../budget/util';
import { Button } from '../common/Button';
import { Link } from '../common/Link';
import { Select } from '../common/Select';
import { View } from '../common/View';
import { usePageTitle } from '../PageTitleContext';
import { SIDEBAR_WIDTH } from '../sidebar/Sidebar';
import { IntersectionBoundary } from '../tooltips';

import { ActionButton } from './ActionButton';
import ActionSelect from './ActionSelect';
import BudgetTemplateCategories from './BudgetTemplateCategories';
import ChartModalButton from './reports/ChartModal';
import { Datasource } from './reports/shared';
import TableHeader from './TableHeader';
import { type CellEditing, type ITableTotals } from './types';
import { getMonthlyBudget, getYearlyBudget, VIEW_MODE } from './util';
import { UndoRedoButtons } from '../common/UndoRedoButtons';

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;
const previousYear = currentYear - 1;
const PERIOD_OPTIONS = [
  [previousYear, `Previous year (${previousYear})`],
  [currentYear, `Current year (${currentYear})`],
  [nextYear, `Next year (${nextYear})`],
] as [number, string][];

export default function BudgetTemplatePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isTablet = useIsTablet();
  const spreadsheet = useSpreadsheet();
  const { grouped: categoryGroups, list: categories } = useCategories(false);

  const [collapsed = [], setCollapsed] = useLocalPref('budget.collapsed');
  const [showHiddenCategories] = useLocalPref('budget.showHiddenCategories');
  const [isInitialized, setIsInitialized] = useState(false);
  const [newCategoryForGroup, setNewCategoryForGroup] = useState(null);
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [budgetLines, setBudgetLines] = useState(null);
  const [, setDirtyCount] = useState(0);
  const [fiscalYearStart] = useGlobalPref('fiscalYearStart');
  const [editing, setEditing] = useState(null);
  const [period, setPeriod] = useState(currentYear);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [mode, setMode] = useState<VIEW_MODE>(VIEW_MODE.ONLY_MONTHS);
  const [, setIsApplyingGlobalBudget] = useState(false);
  const [, setIsApplyingLastYear] = useState(false);

  // Budget data for the displayed fiscal year, keyed as Map<"YYYY-MM", Map<categoryId, amountCents>>.
  // We aggregate this client-side to compute group / page totals that respect
  // the user's "show hidden categories" preference — mirroring how Custom
  // Reports filters hidden items at the aggregation layer rather than relying
  // on the shared `group-budget-X` sheet binding (which always sums every
  // on-budget non-tombstoned category, regardless of hidden state).
  const [budgetRowsByMonthAndCategory, setBudgetRowsByMonthAndCategory] =
    useState<Map<string, Map<string, number>>>(new Map());
  const [budgetTypePref] = useLocalPref('budgetType');

  const budgetCategoriesRef = useRef<HTMLDivElement>();

  const { setPageTitle } = usePageTitle();

  const screenSize = useWindowSize();
  const isAnnualBudgetControlTextsShown =
    mode === VIEW_MODE.COMBINED
      ? screenSize.width > 1500
      : screenSize.width > 1400;

  useEffect(() => {
    if (isTablet) {
      navigate('/budget');
    }
  }, [isTablet]);

  useEffect(() => {
    setPageTitle('Planning');
    initialize();
  }, []);

  useEffect(() => {
    (async () => {
      const date = new Date(period, fiscalYearStart);
      setStartDate(d.format(d.toDate(date), 'yyyy-MM'));
      setEndDate(d.format(d.addMonths(date, 11), 'yyyy-MM'));
    })();
  }, [period]);

  const months = useMemo(() => {
    return monthsOfFiscalYear(period, fiscalYearStart);
  }, [period, fiscalYearStart]);

  const groupTotals: { [groupId: string]: ITableTotals } = useMemo(() => {
    if (!budgetLines) {
      return null;
    }

    const tmpGroupTotals: { [groupId: string]: ITableTotals } = {};

    for (const group of categoryGroups) {
      if (group.hidden && !showHiddenCategories) {
        continue;
      }

      const relevantBudgetLines = budgetLines.filter(line =>
        group.categories.find(
          cat =>
            cat.id === line.category && (showHiddenCategories || !cat.hidden),
        ),
      );

      if (!tmpGroupTotals[group.id]) {
        tmpGroupTotals[group.id] = {
          amount: 0,
          monthly: 0,
          yearly: 0,
        };
      }

      for (const budgetLine of relevantBudgetLines) {
        tmpGroupTotals[group.id].amount += budgetLine.amount;
        tmpGroupTotals[group.id].monthly += budgetLine
          ? getMonthlyBudget(budgetLine)
          : 0;
        tmpGroupTotals[group.id].yearly += budgetLine
          ? getYearlyBudget(budgetLine)
          : 0;
      }
    }

    return tmpGroupTotals;
  }, [budgetLines, categoryGroups, showHiddenCategories]);

  // Subscribe to the budget table for the displayed year. This is the
  // source we aggregate visible-category totals from. The sheet engine's
  // group-budget-X binding can't be used directly because it has no
  // awareness of `cat.hidden` and would over-sum.
  useEffect(() => {
    if (!months || months.length === 0) return;
    const toDbMonth = (m: string) => parseInt(m.replace('-', ''), 10);
    const firstDbMonth = toDbMonth(months[0].month);
    const lastDbMonth = toDbMonth(months[months.length - 1].month);
    const budgetTable =
      (budgetTypePref || 'rollover') === 'report'
        ? 'reflect_budgets'
        : 'zero_budgets';
    const query = q(budgetTable)
      .filter({ month: { $gte: firstDbMonth, $lte: lastDbMonth } })
      .select(['amount', 'month', 'category']);
    const lq = liveQuery(query, (data: { amount: number; month: number; category: string }[]) => {
      const map = new Map<string, Map<string, number>>();
      for (const row of data || []) {
        const m = String(row.month);
        // Convert e.g. 202602 -> "2026-02"
        const formatted = `${m.slice(0, 4)}-${m.slice(4)}`;
        let inner = map.get(formatted);
        if (!inner) {
          inner = new Map();
          map.set(formatted, inner);
        }
        inner.set(row.category, row.amount || 0);
      }
      setBudgetRowsByMonthAndCategory(map);
    });
    return () => lq.unsubscribe();
  }, [budgetTypePref, months]);

  // Aggregate visible-category budget totals client-side. Hidden categories
  // are excluded when `budget.showHiddenCategories` is off, so the displayed
  // group/page/year totals always equal the sum of the visible row values
  // (the same invariant Custom Reports preserves via filterHiddenItems).
  const visibleTotals = useMemo(() => {
    const groupMonthBudget = new Map<string, Map<string, number>>();
    const groupYearBudget = new Map<
      string,
      { total: number; monthlyAvg: number }
    >();
    const pageMonth = new Map<
      string,
      { income: number; expense: number; saved: number }
    >();

    if (!months || months.length === 0 || !categoryGroups) {
      return {
        groupMonthBudget,
        groupYearBudget,
        pageMonth,
        pageYear: {
          income: 0,
          expense: 0,
          saved: 0,
          incomeMonthlyAvg: 0,
          expenseMonthlyAvg: 0,
          savedMonthlyAvg: 0,
        },
      };
    }

    for (const m of months) {
      pageMonth.set(m.month, { income: 0, expense: 0, saved: 0 });
    }

    for (const group of categoryGroups) {
      const visibleCats = (group.categories || []).filter(
        cat => showHiddenCategories || !cat.hidden,
      );
      const monthMap = new Map<string, number>();
      let yearTotal = 0;

      for (const m of months) {
        const monthData = budgetRowsByMonthAndCategory.get(m.month);
        let sum = 0;
        if (monthData) {
          for (const cat of visibleCats) {
            sum += monthData.get(cat.id) || 0;
          }
        }
        monthMap.set(m.month, sum);
        yearTotal += sum;

        const page = pageMonth.get(m.month)!;
        if (group.is_income) {
          page.income += sum;
        } else {
          page.expense += sum;
        }
      }
      groupMonthBudget.set(group.id, monthMap);
      groupYearBudget.set(group.id, {
        total: yearTotal,
        monthlyAvg: yearTotal / 12,
      });
    }

    let yearIncome = 0;
    let yearExpense = 0;
    for (const m of months) {
      const page = pageMonth.get(m.month)!;
      page.saved = page.income - page.expense;
      yearIncome += page.income;
      yearExpense += page.expense;
    }
    const yearSaved = yearIncome - yearExpense;

    return {
      groupMonthBudget,
      groupYearBudget,
      pageMonth,
      pageYear: {
        income: yearIncome,
        expense: yearExpense,
        saved: yearSaved,
        incomeMonthlyAvg: yearIncome / 12,
        expenseMonthlyAvg: yearExpense / 12,
        savedMonthlyAvg: yearSaved / 12,
      },
    };
  }, [
    budgetRowsByMonthAndCategory,
    categoryGroups,
    months,
    showHiddenCategories,
  ]);

  useEffect(() => {
    if (!isInitialized) {
      return;
    }
  }, [isInitialized]);

  function loadCategories() {
    dispatch(getCategories());
  }

  async function initialize() {
    loadCategories();
    const today = new Date();
    await prewarmAllMonths(
      'report',
      spreadsheet,
      {
        start: d.format(today, 'yyyy-MM'),
        end: d.format(d.addYears(today, 2), 'yyyy-MM'),
      },
      d.format(today, 'yyyy-MM'),
    );

    const query = q('budget_template_lines')
      .filter({
        $or: [
          {
            'category.tombstone': false,
          },
          {
            'category.tombstone': null,
          },
        ],
      })
      .select('*');

    liveQuery(query, data => {
      setBudgetLines(data);
      setDirtyCount(data.filter(o => o.is_dirty).length);
      setIsInitialized(true);
    }); // @TODO: handle error
  }

  const categoryNameAlreadyExistsNotification = name => {
    dispatch(
      addNotification({
        type: 'error',
        message: `Category ‘${name}’ already exists in group (May be Hidden)`,
      }),
    );
  };

  async function setBudgetLinesForPeriodAvg(value: string) {
    const isAllTime = value === 'all-time';
    await send('budget-template/set-period-avg', {
      monthsCount: value,
      isAllTime,
    });
  }

  function onBudgetLineChange(budgetLine: Partial<BudgetTemplateLine>) {
    // Optimistic update so monthly/yearly summary columns reflect changes immediately
    setBudgetLines((current: any) => {
      if (!current) return current;
      const idx = current.findIndex((l: any) => l.category === budgetLine.category);
      if (idx !== -1) {
        const updated = [...current];
        updated[idx] = { ...updated[idx], ...budgetLine };
        return updated;
      }
      return [...current, budgetLine];
    });
    // Fire and forget the actual save
    send('budget-template/upsert', { budgetLine });
  }

  async function onBudgetLineApply(
    budgetLine: Partial<BudgetTemplateLine>,
    mode: ApplyBudgetTemplateMode,
  ) {
    await send('budget-template/apply-line', {
      budgetLine,
      startDate,
      endDate,
      mode,
    });
  }

  async function applyBudgetGlobally(mode: ApplyBudgetTemplateMode) {
    setIsApplyingGlobalBudget(true);
    await send('budget-template/apply-global', { startDate, endDate, mode });
    setIsApplyingGlobalBudget(false);
  }

  function onBudgetAction(month: string, type: string, args: unknown) {
    return dispatch(applyBudgetAction(month, type, args));
  }

  async function onEditCell(cell?: CellEditing) {
    setEditing(cell);
  }

  function onEditName(id: string) {
    setEditing(id ? { id, column: 'name' } : null);
  }

  function toggleHiddenCategories() {
    dispatch((innerDispatch, getState) => {
      const { prefs } = getState();
      const showHiddenCategories = prefs.local['budget.showHiddenCategories'];
      innerDispatch(
        savePrefs({
          'budget.showHiddenCategories': !showHiddenCategories,
        }),
      );
    });
  }

  function expandAllCategories() {
    setCollapsed([]);
  }

  function collapseAllCategories() {
    setCollapsed(categoryGroups.map(g => g.id));
  }

  // Duplicated with budget components
  const onSaveCategory = async category => {
    const cats = await send('get-categories');

    const exists =
      cats.grouped
        .filter(g => g.id === category.cat_group)[0]
        .categories.filter(
          c => c.name.toUpperCase() === category.name.toUpperCase(),
        )
        .filter(c => (category.id === 'new' ? true : c.id !== category.id))
        .length > 0;

    if (exists) {
      categoryNameAlreadyExistsNotification(category.name);
      return;
    }

    if (category.id === 'new') {
      dispatch(
        createCategory(
          category.name,
          category.cat_group,
          category.is_income,
          category.hidden,
        ),
      );
      onHideNewCategory();
    } else {
      dispatch(updateCategory(category));
    }
  };

  // Duplicated with budget components
  const onDeleteCategory = async id => {
    const mustTransfer = await send('must-category-transfer', { id });

    if (mustTransfer) {
      dispatch(
        pushModal('confirm-category-delete', {
          category: id,
          onDelete: transferCategory => {
            if (id !== transferCategory) {
              dispatch(deleteCategory(id, transferCategory));
            }
          },
        }),
      );
    } else {
      dispatch(deleteCategory(id));
    }
  };

  // Duplicated with budget components
  const onSaveGroup = async group => {
    if (group.id === 'new') {
      dispatch(createGroup(group.name, group.isIncome ?? false, group.isDonation ?? false));
      onHideNewGroup();
    } else {
      dispatch(updateGroup(group));
    }
  };

  // Duplicated with budget components
  const onDeleteGroup = async id => {
    const group = categoryGroups.find(g => g.id === id);

    let mustTransfer = false;
    for (const category of group.categories) {
      if (await send('must-category-transfer', { id: category.id })) {
        mustTransfer = true;
        break;
      }
    }

    if (mustTransfer) {
      dispatch(
        pushModal('confirm-category-delete', {
          group: id,
          onDelete: transferCategory => {
            dispatch(deleteGroup(id, transferCategory));
          },
        }),
      );
    } else {
      dispatch(deleteGroup(id));
    }
  };

  // Duplicated with budget components
  async function onReorderCategory(sortInfo) {
    const cats = await send('get-categories');
    const moveCandidate = cats.list.filter(c => c.id === sortInfo.id)[0];
    const exists =
      cats.grouped
        .filter(g => g.id === sortInfo.groupId)[0]
        .categories.filter(
          c => c.name.toUpperCase() === moveCandidate.name.toUpperCase(),
        )
        .filter(c => c.id !== moveCandidate.id).length > 0;

    if (exists) {
      categoryNameAlreadyExistsNotification(moveCandidate.name);
      return;
    }

    dispatch(moveCategory(sortInfo.id, sortInfo.groupId, sortInfo.targetId));
  }

  // Duplicated with budget components
  async function onReorderGroup(sortInfo) {
    dispatch(moveCategoryGroup(sortInfo.id, sortInfo.targetId));
  }

  // Duplicated with budget components
  const onShowNewCategory = (groupId: string) => {
    setNewCategoryForGroup(groupId);
    setCollapsed(collapsed.filter(c => c !== groupId));
  };

  // Duplicated with budget components
  const onHideNewCategory = () => {
    setNewCategoryForGroup(null);
  };

  // Duplicated with budget components
  const onShowNewGroup = () => {
    setIsAddingGroup(true);
  };

  // Duplicated with budget components
  const onHideNewGroup = () => {
    setIsAddingGroup(false);
  };

  // Duplicated with budget components
  const moveVertically = (dir: number) => {
    const flattened = categoryGroups.reduce((all, group) => {
      if (collapsed.includes(group.id)) {
        return all.concat({ id: group.id, isGroup: true });
      }
      return all.concat([{ id: group.id, isGroup: true }, ...group.categories]);
    }, []);

    if (editing) {
      const idx = flattened.findIndex(item => item.id === editing.id);
      let nextIdx = idx + dir;

      while (nextIdx >= 0 && nextIdx < flattened.length) {
        const next = flattened[nextIdx];

        if (next.isGroup) {
          nextIdx += dir;
        } else {
          onEditCell({
            id: next.id,
            column: editing.column,
            monthIndex: editing.monthIndex,
          });
          return;
        }
      }
    }
  };

  const moveHorizontally = (dir: number) => {
    const flattened = categoryGroups.reduce((all, group) => {
      if (collapsed.includes(group.id)) {
        return all.concat({ id: group.id, isGroup: true });
      }
      return all.concat([{ id: group.id, isGroup: true }, ...group.categories]);
    }, []);

    if (editing) {
      let nextMonthIndex;
      let nextColumn;

      let nextIdx = flattened.findIndex(item => item.id === editing.id);

      switch (editing.column) {
        case 'frequency':
          if (dir < 0) {
            nextColumn = 'month';
            nextMonthIndex = months.length - 1;
            nextIdx = nextIdx + dir;
          } else {
            nextColumn = 'amount';
            nextMonthIndex = null;
          }
          break;
        case 'amount':
          if (dir < 0) {
            nextColumn = 'frequency';
            nextMonthIndex = null;
          } else {
            nextColumn = 'month';
            nextMonthIndex = 0;
          }
          break;
        case 'month':
          if (dir < 0 && editing.monthIndex === 0) {
            nextColumn = 'amount';
            nextMonthIndex = null;
          } else {
            nextColumn = 'month';
            nextMonthIndex = editing.monthIndex + dir;

            if (nextMonthIndex > months.length - 1) {
              nextIdx = nextIdx + dir;
              nextColumn = 'frequency';
              nextMonthIndex = null;
            }
          }
          break;
      }

      budgetCategoriesRef.current?.scroll({
        left: nextIdx * 20,
        behavior: 'auto',
      });

      while (nextIdx >= 0 && nextIdx < flattened.length) {
        const next = flattened[nextIdx];

        if (next.isGroup) {
          nextIdx += dir;
        } else {
          onEditCell({
            id: next.id,
            column: nextColumn,
            monthIndex: nextMonthIndex,
          });
          return;
        }
      }
    }
  };

  // Duplicated with budget components
  const onKeyDown = e => {
    if (!editing) {
      return null;
    }

    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        moveVertically(e.shiftKey ? -1 : 1);
        break;
      case 'Tab':
        e.preventDefault();
        moveHorizontally(e.shiftKey ? -1 : 1);
        break;
    }
  };

  function getLabel(mode: VIEW_MODE) {
    switch (mode) {
      case VIEW_MODE.COMBINED:
        return 'Hide budget template';
      case VIEW_MODE.ONLY_MONTHS:
        return 'Populate using budget template';
    }
  }

  if (!isInitialized) {
    return <>Loading</>;
  }

  return (
    <View style={{ flexDirection: 'column', flex: 1 }}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent:
            mode === VIEW_MODE.COMBINED ? 'space-between' : 'flex-end',
          margin: 20,
          marginBottom: 0,
        }}
      >
        {mode === VIEW_MODE.COMBINED && (
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'flex-start',
              width: 750,
            }}
          >
            <View
              data-adoption-id="apply-budget-global"
              style={{ flexDirection: 'row', gap: 5 }}
            >
              <ActionButton
                icon={
                  <SvgBudgetTemplateApply width={15} style={{ marginRight: 4 }} />
                }
                label="Apply budget template"
                showLabel={isAnnualBudgetControlTextsShown}
                onClick={() => {
                  applyBudgetGlobally(ApplyBudgetTemplateMode.Standard);
                }}
              />
              <ActionButton
                icon={
                  <SvgBudgetTemplateOverwrite
                    width={15}
                    style={{ marginRight: 4 }}
                  />
                }
                label="Overwrite with budget template"
                showLabel={isAnnualBudgetControlTextsShown}
                onClick={() => {
                  dispatch(
                    pushModal('confirm-generic', {
                      text: `Proceeding will apply the budget template values to the ${period} year budget, overwriting any existing values. Are you sure you want to continue?`,
                      onConfirm: () => {
                        applyBudgetGlobally(ApplyBudgetTemplateMode.Overwrite);
                      },
                    }),
                  );
                }}
              />
            </View>
          </View>
        )}
        <View
          style={{
            flex: 'none',
            justifyContent: 'flex-end',
            flexDirection: 'row',
          }}
        >
          <View
            style={{ flexDirection: 'row', gap: 5 }}
            data-adoption-id="apply-options"
          >
            {/* Unified Populate / Hide toggle — always lives in the right group
                so it doesn't jump across the screen when the mode changes. */}
            <ActionButton
              icon={
                mode === VIEW_MODE.COMBINED ? (
                  <SvgViewHide width={15} style={{ marginRight: 4 }} />
                ) : (
                  <SvgBudgetTemplatePopulateUsing
                    width={15}
                    style={{ marginRight: 4 }}
                  />
                )
              }
              label={
                mode === VIEW_MODE.COMBINED
                  ? 'Hide budget template'
                  : 'Populate using budget template'
              }
              showLabel={isAnnualBudgetControlTextsShown}
              onClick={() =>
                setMode(
                  mode === VIEW_MODE.COMBINED
                    ? VIEW_MODE.ONLY_MONTHS
                    : VIEW_MODE.COMBINED,
                )
              }
            />
            <ActionSelect
              icon={<SvgArrowBackUp width={15} />}
              options={[
                ['apply-previous-year-actual', 'Previous year actual'],
                ['apply-previous-year-actual-avg', 'Previous year actual average'],
                ['apply-previous-year-budget', 'Previous year budget'],
                ['apply-previous-year-budget-avg', 'Previous year budget average'],
              ]}
              defaultLabel="Apply from previous year"
              showLabel={isAnnualBudgetControlTextsShown}
              menuOffsetLeft={-25}
              onChange={option => {
                const previousYear = period - 1;
                const sourceLabels: Record<string, string> = {
                  'apply-previous-year-actual': `previous year (${previousYear}) actuals`,
                  'apply-previous-year-actual-avg': `the average of previous year (${previousYear}) actuals`,
                  'apply-previous-year-budget': `the previous year (${previousYear}) budget`,
                  'apply-previous-year-budget-avg': `the average of the previous year (${previousYear}) budget`,
                };
                const handlers: Record<string, () => void> = {
                  'apply-previous-year-actual': onApplyPreviousYearActual,
                  'apply-previous-year-actual-avg':
                    onApplyPreviousYearActualAverage,
                  'apply-previous-year-budget': onApplyPreviousYearBudget,
                  'apply-previous-year-budget-avg':
                    onApplyPreviousYearBudgetAverage,
                };
                const handler = handlers[option];
                if (!handler) return;
                dispatch(
                  pushModal('confirm-generic', {
                    text: `Proceeding will overwrite the entire ${period} year budget with ${sourceLabels[option]}. Are you sure you want to continue?`,
                    onConfirm: handler,
                  }),
                );
              }}
            />
            <ActionSelect
              icon={<SvgBarChart width={15} />}
              options={[
                ['3', '3 months'],
                ['6', '6 months'],
                ['12', '12 months'],
                ['all-time', 'All time'],
              ]}
              defaultLabel="Set average for a period"
              showLabel={isAnnualBudgetControlTextsShown}
              onChange={value => {
                const periodLabel =
                  value === 'all-time'
                    ? 'all time'
                    : `the last ${value} months`;
                dispatch(
                  pushModal('confirm-generic', {
                    text: `Proceeding will overwrite the entire ${period} year budget with the average of ${periodLabel}. Are you sure you want to continue?`,
                    onConfirm: () => {
                      if (value === 'all-time') {
                        setBudget('set-all-time-avg');
                      } else {
                        setBudget(`set-${value}-avg`, {
                          pivotMonth: monthUtils.currentMonth(),
                        });
                      }
                    },
                  }),
                );
              }}
            />
            <ActionButton
              icon={
                <SvgBudgetTemplateReset width={15} style={{ marginRight: 4 }} />
              }
              label="Reset budget to zero"
              showLabel={isAnnualBudgetControlTextsShown}
              onClick={() =>
                dispatch(
                  pushModal('confirm-generic', {
                    text: `Proceeding will reset all monthly budgets to zero for the entire ${period} year. Are you sure you want to continue?`,
                    onConfirm: () => setBudget('set-zero'),
                  }),
                )
              }
            />
          </View>
        </View>
      </View>

      <View
        style={{
          flex: 1,
          ...(styles.lightScrollbar && {
            '& ::-webkit-scrollbar': {
              backgroundColor: 'transparent',
            },
            '& ::-webkit-scrollbar-thumb': {
              backgroundColor: 'black',
            },
          }),
        }}
      >
        <View
          style={{
            margin: '20px 20px 10px 20px',
            backgroundColor: theme.tableBackground,
            overflow: 'hidden',
            boxShadow: styles.cardShadow,
            borderRadius: '0 0 4px 4px',
            padding: '20px',
          }}
        >
          <View style={{ flexDirection: 'row', marginTop: 10, flex: 'none' }}>
            {mode !== VIEW_MODE.ONLY_MONTHS ? (
              <View
                style={{
                  flex: 1,
                  paddingRight: 15,
                  borderRightWidth: 1,
                  borderColor: theme.tableBorder,
                  minWidth: 742,
                  maxWidth: 742,
                }}
              >
                <label style={{ fontWeight: 'bold', fontSize: 16 }}>
                  Budget template
                </label>

                <View
                  style={{
                    justifyContent: 'flex-end',
                    alignItems: 'flex-end',
                    flexDirection: 'column',
                    gap: 5,
                  }}
                >
                  <label />
                  <ChartModalButton
                    year={period}
                    categoryGroups={categoryGroups}
                    categories={categories}
                    groupTotals={groupTotals}
                    budgetLines={budgetLines}
                    datasource={Datasource.BudgetTemplate}
                  />
                </View>
              </View>
            ) : null}
            <View style={{ flex: 1, flexDirection: 'row' }}>
              <View style={{ flex: 1 }}>
                <View
                  style={{
                    flexDirection: 'row',
                    paddingLeft: mode === VIEW_MODE.COMBINED ? 15 : 0,
                    marginBottom: 15,
                    gap: 12,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                  }}
                >
                  <View
                    style={{
                      flexDirection: screenSize.width < 1500 ? 'column' : 'row',
                      gap: 8,
                      alignItems: 'flex-end',
                    }}
                  >
                    <View style={{ flexDirection: 'column', gap: 4 }}>
                      <label>Planning year:</label>
                      <Select
                        options={PERIOD_OPTIONS.map(o => [o[0] + '', o[1]])}
                        value={period + ''}
                        defaultLabel="Select an option"
                        onChange={option => {
                          setPeriod(parseInt(option));
                        }}
                      />
                    </View>
                    {(window as any).FISCAL_YEAR_FEATURE_ENABLED ? (
                      <View
                        style={{ margin: '0 5px 5px' }}
                        title="Set start of fiscal year"
                      >
                        <Link
                          variant="internal"
                          to="/settings?highlight=fiscal-year-start"
                        >
                          <SvgCog width={15} height={15} />
                        </Link>
                      </View>
                    ) : null}
                    <View style={{ marginBottom: 8 }}>
                      <ChartModalButton
                        year={period}
                        budgetLines={budgetLines}
                        categoryGroups={categoryGroups}
                        categories={categories}
                        groupTotals={groupTotals}
                        datasource={Datasource.AnnualGrid}
                      />
                    </View>
                  </View>
                </View>
              </View>

              <label style={{ fontWeight: 'bold', fontSize: 16 }}>
                {' '}
                Annual Budget{' '}
              </label>
            </View>
          </View>
          
          <View>
            <UndoRedoButtons />
          </View>

          <View
            data-adoption-id="planning-table-view"
            style={{
              maxHeight: 'calc(100vh - 300px)',
            }}
          >
            <TableHeader
              year={period}
              categoryGroups={categoryGroups}
              groupTotals={groupTotals}
              visibleTotals={visibleTotals}
              viewMode={mode}
              months={months}
              toggleHiddenCategories={toggleHiddenCategories}
              collapseAllCategories={collapseAllCategories}
              expandAllCategories={expandAllCategories}
            />

            <IntersectionBoundary.Provider value={budgetCategoriesRef}>
              <View
                data-adoption-id="budget-template-scroll-area"
                innerRef={budgetCategoriesRef}
                style={{
                  overflowY: 'scroll',
                  overflowAnchor: 'none',
                  paddingBottom: '17px', // Needed for horizontal scrollbar, as it has -17px margin-bottom
                }}
                onScroll={(evt: React.UIEvent<HTMLElement>) => {
                  // nasty stuff here, but there was no way to have a smooth scrolling
                  // by passing scroll position through React means
                  const headerElem = document.getElementById(
                    'table-header-months-scroller',
                  );
                  if (headerElem) {
                    headerElem.scroll({
                      left: evt.currentTarget.scrollLeft,
                      behavior: 'auto',
                    });
                  }
                }}
              >
                <View
                  onKeyDown={onKeyDown}
                  style={{
                    // opacity: draggingState ? 0.5 : 1,
                    flexShrink: 0,
                  }}
                >
                  <View
                    data-adoption-id="planning-category-list"
                    style={{
                      zIndex: -1000,
                      position: 'fixed',
                      width: 200,
                      opacity: 0,
                      height: window.innerHeight - 310,
                      maxHeight: 'calc(71vh)',
                    }}
                  />
                  <View
                    data-adoption-id="planning-budget-group-list"
                    style={{
                      zIndex: -1000,
                      position: 'fixed',
                      width: 740,
                      opacity: 0,
                      height: window.innerHeight - 310,
                      maxHeight: 'calc(71vh)',
                    }}
                  />
                  <View
                    data-adoption-id="planning-budget-annual-list"
                    style={{
                      zIndex: -1000,
                      position: 'fixed',
                      left: 740 + SIDEBAR_WIDTH + 35,
                      width: window.innerWidth - 740 - SIDEBAR_WIDTH - 70,
                      opacity: 0,
                      height: window.innerHeight - 310,
                      maxHeight: 'calc(71vh)',
                    }}
                  />
                  <BudgetTemplateCategories
                    year={period}
                    categoryGroups={categoryGroups}
                    budgetLines={budgetLines}
                    groupTotals={groupTotals}
                    visibleTotals={visibleTotals}
                    months={months}
                    viewMode={mode}
                    editingCell={editing}
                    newCategoryForGroup={newCategoryForGroup}
                    isAddingGroup={isAddingGroup}
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    onBudgetLineChange={onBudgetLineChange}
                    onBudgetLineApply={onBudgetLineApply}
                    onEditName={onEditName}
                    onEditCell={onEditCell} // Previously was onEditMonth
                    onSaveGroup={onSaveGroup}
                    onShowNewCategory={onShowNewCategory}
                    onHideNewCategory={onHideNewCategory}
                    onSaveCategory={onSaveCategory}
                    onDeleteCategory={onDeleteCategory}
                    onDeleteGroup={onDeleteGroup}
                    onReorderCategory={onReorderCategory}
                    onReorderGroup={onReorderGroup}
                    onShowNewGroup={onShowNewGroup}
                    onHideNewGroup={onHideNewGroup}
                    onBudgetAction={onBudgetAction}
                  />
                  <View
                    style={{
                      flexDirection: 'row',
                      flex: 1,
                      minWidth: 'fit-content',
                    }}
                  >
                    <View
                      style={{
                        width: 200,
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                        position: 'sticky',
                        left: 0,
                        backgroundColor: '#FFF',
                        zIndex: 1000,
                      }}
                    >
                      <Button
                        data-adoption-id="add-group-btn"
                        onClick={onShowNewGroup}
                        style={{ fontSize: 12, margin: 10 }}
                      >
                        Add Group
                      </Button>
                    </View>
                  </View>
                </View>
              </View>
            </IntersectionBoundary.Provider>
          </View>
        </View>
      </View>
    </View>
  );

  async function setBudget(type: string, args = {}) {
    for (const month of months) {
      await onBudgetAction(month.month, type, args);
    }
  }

  async function onApplyPreviousYearActual() {
    setIsApplyingLastYear(true);
    try {
      for (const { month } of months) {
        await send('budget/apply-previous-year-actual', {
          year: period,
          startMonth: fiscalYearStart,
          month,
        });
      }
    } catch (error) {
      console.error('Error applying previous year actual:', error);
    } finally {
      setIsApplyingLastYear(false);
    }
  }

  async function onApplyPreviousYearActualAverage() {
    setIsApplyingLastYear(true);
    try {
      const cacheKey = `${period}:${fiscalYearStart}:${Date.now()}`;
      for (const { month } of months) {
        await send('budget/apply-previous-year-actual-avg', {
          year: period,
          startMonth: fiscalYearStart,
          month,
          cacheKey,
        });
      }
    } catch (error) {
      console.error('Error applying previous year actual average:', error);
    } finally {
      setIsApplyingLastYear(false);
    }
  }

  async function onApplyPreviousYearBudgetAverage() {
    setIsApplyingLastYear(true);
    try {
      const cacheKey = `${period}:${fiscalYearStart}:${Date.now()}`;
      for (const { month } of months) {
        await send('budget/apply-previous-year-budget-avg', {
          year: period,
          startMonth: fiscalYearStart,
          month,
          cacheKey,
        });
      }
    } catch (error) {
      console.error('Error applying previous year budget average:', error);
    } finally {
      setIsApplyingLastYear(false);
    }
  }

  async function onApplyPreviousYearBudget() {
    setIsApplyingLastYear(true);
    try {
      for (const { month } of months) {
        await send('budget/apply-previous-year-budget', {
          year: period,
          startMonth: fiscalYearStart,
          month,
        });
      }
    } catch (error) {
      console.error('Error applying previous year budget:', error);
    } finally {
      setIsApplyingLastYear(false);
    }
  }

  async function onLastYearActualApply() {
    setIsApplyingLastYear(true);
    for (const catGroup of categoryGroups) {
      for (const category of catGroup.categories) {
        for (const month of months) {
          const lastYear = monthUtils.prevYear(month.month);
          const sheetName = monthUtils.sheetForMonth(lastYear);
          const monthlyBudget = await spreadsheet.get(
            sheetName,
            reportBudget.catSumAmount(category.id),
          );

          if (!catGroup.is_income) {
            monthlyBudget.value = -monthlyBudget.value;
          }

          onBudgetAction(month.month, 'budget-amount', {
            category: category.id,
            amount: monthlyBudget.value,
          });
        }
      }
    }
    setIsApplyingLastYear(false);
  }
}
